/**
 * FASWORLD product — variant sync, gallery thumbs, add to cart
 */
(() => {
  const formatMoney = (cents) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    const value = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'QAR',
        currencyDisplay: 'code',
      }).format(value);
    } catch {
      return `QAR ${value.toFixed(2)}`;
    }
  };

  const initProduct = (root) => {
    if (!root || root.dataset.fwProductReady === 'true') return;
    root.dataset.fwProductReady = 'true';

    const jsonEl = root.querySelector('[data-fw-product-json]');
    if (!jsonEl) return;

    let product;
    try {
      product = JSON.parse(jsonEl.textContent);
    } catch {
      return;
    }

    const stringsFallback = {
      inStock: 'In stock',
      soldOut: 'Sold out',
      unavailable: 'Unavailable',
      addToCart: 'Add to cart',
      addingToCart: 'Adding…',
      buyNow: 'Buy now',
      redirecting: 'Redirecting…',
      addToCartError: 'Could not add to cart',
    };
    const stringsEl = root.querySelector('[data-fw-product-strings]');
    let strings = stringsFallback;
    if (stringsEl) {
      try {
        strings = { ...stringsFallback, ...JSON.parse(stringsEl.textContent) };
      } catch {
        strings = stringsFallback;
      }
    }

    const optionGroups = [...root.querySelectorAll('[data-fw-product-option]')];
    const optionInputs = [...root.querySelectorAll('[data-fw-product-option-input]')];
    const optionsRoot = root.querySelector('[data-fw-product-options]');
    if (optionsRoot) optionsRoot.hidden = false;
    const variantInput = root.querySelector('[data-fw-product-variant-id]');
    if (variantInput && !variantInput.getAttribute('name')) {
      variantInput.setAttribute('name', 'id');
    }
    const priceCurrent = root.querySelector('[data-fw-product-price-current]');
    const priceCompare = root.querySelector('[data-fw-product-price-compare]');
    const priceWrap = root.querySelector('[data-fw-product-price]');
    const availability = root.querySelector('[data-fw-product-availability]');
    const qtyInput = root.querySelector('[data-fw-product-qty]');
    const addBtn = root.querySelector('[data-fw-add-to-cart]');
    const buyBtn = root.querySelector('[data-fw-buy-now]');
    const mainImage = root.querySelector('[data-fw-product-main-image]');
    const thumbs = [...root.querySelectorAll('[data-fw-product-thumb]')];
    const nativeShareButtons = [...root.querySelectorAll('[data-fw-product-share-native]')];
    const platformShareLinks = [...root.querySelectorAll('[data-fw-product-share-platform]')];
    const shareStatus = root.querySelector('[data-fw-product-share-status]');

    const getSelectedOptions = () =>
      optionGroups.map((group) => {
        const checked = group.querySelector('[data-fw-product-option-input]:checked');
        return checked?.value ?? '';
      });

    const findVariant = (selected = getSelectedOptions()) => {
      if (!product.variants || product.variants.length === 0) return null;
      if (optionGroups.length === 0) return product.variants[0];

      return (
        product.variants.find((variant) =>
          selected.every((value, index) => String(variant[`option${index + 1}`]) === String(value))
        ) || null
      );
    };

    const updateOptionAvailability = (selected) => {
      optionGroups.forEach((group, groupIndex) => {
        const inputs = [...group.querySelectorAll('[data-fw-product-option-input]')];

        inputs.forEach((input) => {
          const candidate = selected.map((value, index) =>
            index === groupIndex ? input.value : value
          );
          const matchingVariants = (product.variants || []).filter((variant) =>
            candidate.every((value, index) => String(variant[`option${index + 1}`]) === String(value))
          );
          const exists = matchingVariants.length > 0;
          const available = matchingVariants.some((variant) => variant.available);
          const label = input.closest('.fw-product__option-choice');

          input.dataset.available = available ? 'true' : 'false';
          input.dataset.exists = exists ? 'true' : 'false';

          // Visual only — keep radios operable so sold-out and impossible
          // combinations remain inspectable; ATC/availability copy reflects state.
          label?.classList.toggle('is-unavailable', !exists || !available);
        });
      });
    };

    const updateSelectedLabels = () => {
      optionGroups.forEach((group) => {
        const checked = group.querySelector('[data-fw-product-option-input]:checked');
        const selectedLabel = group.querySelector('[data-fw-option-selected]');
        if (selectedLabel && checked) {
          selectedLabel.textContent = checked.value;
        }
      });
    };

    const setMainImage = (src, srcset) => {
      if (!mainImage || !src) return;
      mainImage.src = src;
      if (srcset) mainImage.srcset = srcset;
    };

    const setActiveThumb = (mediaId) => {
      thumbs.forEach((thumb) => {
        const active = String(thumb.dataset.mediaId) === String(mediaId);
        thumb.classList.toggle('is-active', active);
        if (active) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    };

    const updateVariantMedia = (variant) => {
      if (!variant?.featured_media) return;
      const media = variant.featured_media;
      const mediaId = media.id;
      const matchingThumb = thumbs.find((thumb) => String(thumb.dataset.mediaId) === String(mediaId));
      if (matchingThumb) {
        setMainImage(matchingThumb.dataset.mediaSrc, matchingThumb.dataset.mediaSrcset);
        setActiveThumb(mediaId);
        return;
      }
      if (media.preview_image?.src) {
        setMainImage(media.preview_image.src);
        setActiveThumb(mediaId);
      }
    };

    const syncVariant = () => {
      const selected = getSelectedOptions();
      updateSelectedLabels();
      updateOptionAvailability(selected);

      const variant = findVariant(selected);
      if (!variant) {
        if (addBtn) {
          addBtn.disabled = true;
          addBtn.textContent = strings.unavailable;
        }
        if (buyBtn) {
          buyBtn.disabled = true;
        }
        if (availability) {
          availability.textContent = strings.unavailable;
          availability.dataset.available = 'false';
        }
        return;
      }

      if (variantInput) variantInput.value = variant.id;

      if (priceCurrent) priceCurrent.textContent = formatMoney(variant.price);

      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (priceCompare) {
        if (onSale) {
          priceCompare.hidden = false;
          priceCompare.textContent = formatMoney(variant.compare_at_price);
        } else {
          priceCompare.hidden = true;
          priceCompare.textContent = '';
        }
      }
      if (priceWrap) priceWrap.classList.toggle('fw-product__price-sale', Boolean(onSale));

      if (availability) {
        availability.textContent = variant.available ? strings.inStock : strings.soldOut;
        availability.dataset.available = variant.available ? 'true' : 'false';
      }

      if (addBtn) {
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available ? strings.addToCart : strings.soldOut;
      }

      if (buyBtn) {
        buyBtn.disabled = !variant.available;
      }

      updateVariantMedia(variant);
      updateShareLinks(variant.id);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url);
    };

    const openCartDrawer = () => {
      document.dispatchEvent(new CustomEvent('fw:cart:open'));
    };

    const addToCart = async ({ checkout = false } = {}) => {
      const trigger = checkout ? buyBtn : addBtn;
      if (!trigger || trigger.disabled) return;
      const variantId = Number(variantInput?.value);
      const quantity = Math.max(1, Number(qtyInput?.value) || 1);
      if (!variantId) return;

      const buttons = [addBtn, buyBtn].filter(Boolean);
      buttons.forEach((btn) => {
        btn.disabled = true;
      });
      const originalLabel = trigger.textContent;
      trigger.textContent = checkout ? strings.redirecting : strings.addingToCart;

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity }],
          }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || error.message || strings.addToCartError);
        }

        document.dispatchEvent(new CustomEvent('fw:cart:updated'));

        if (checkout) {
          window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
          return;
        }

        openCartDrawer();
      } catch (error) {
        console.error(error);
        window.alert(error.message || strings.addToCartError);
      } finally {
        if (checkout) return;
        const variant = findVariant();
        const available = Boolean(variant?.available);
        if (addBtn) {
          addBtn.disabled = !available;
          addBtn.textContent = available ? originalLabel || strings.addToCart : strings.soldOut;
        }
        if (buyBtn) {
          buyBtn.disabled = !available;
          buyBtn.textContent = strings.buyNow;
        }
      }
    };

    const shareBase =
      root.querySelector('[data-fw-product-share-base]')?.dataset.fwProductShareBase ||
      `${window.location.origin}${product.url}`;

    const getProductShareUrl = (variantId = variantInput?.value) => {
      if (!variantId) return shareBase;
      return `${shareBase}?variant=${variantId}`;
    };

    const buildPlatformShareHref = (platform, shareUrl) => {
      const encodedUrl = encodeURIComponent(shareUrl);
      const encodedTitle = encodeURIComponent(product.title);

      const platforms = {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
        x: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
        skype: `https://web.skype.com/share?url=${encodedUrl}&text=${encodedTitle}`,
        telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`,
        whatsapp: `https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`,
      };

      return platforms[platform] || shareUrl;
    };

    const updateShareLinks = (variantId = variantInput?.value) => {
      const shareUrl = getProductShareUrl(variantId);
      platformShareLinks.forEach((link) => {
        link.href = buildPlatformShareHref(link.dataset.fwProductSharePlatform, shareUrl);
      });
    };

    let shareStatusTimer = 0;
    const setShareStatus = (message) => {
      if (!shareStatus) return;
      shareStatus.textContent = message;
      window.clearTimeout(shareStatusTimer);
      shareStatusTimer = window.setTimeout(() => {
        shareStatus.textContent = '';
      }, 2500);
    };

    const shareProduct = async () => {
      const shareData = {
        title: product.title,
        url: getProductShareUrl(),
      };

      if (typeof navigator.share === 'function') {
        try {
          await navigator.share(shareData);
        } catch (error) {
          if (error?.name !== 'AbortError') console.error(error);
        }
        return;
      }

      try {
        await navigator.clipboard.writeText(shareData.url);
        setShareStatus('Product link copied');
      } catch (error) {
        console.error(error);
        setShareStatus('Unable to copy product link');
      }
    };

    optionInputs.forEach((input) => {
      input.addEventListener('change', syncVariant);
    });

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        setMainImage(thumb.dataset.mediaSrc, thumb.dataset.mediaSrcset);
        setActiveThumb(thumb.dataset.mediaId);
      });
    });

    if (addBtn) {
      addBtn.addEventListener('click', (event) => {
        event.preventDefault();
        addToCart({ checkout: false });
      });
    }

    if (buyBtn) {
      buyBtn.addEventListener('click', (event) => {
        event.preventDefault();
        addToCart({ checkout: true });
      });
    }

    nativeShareButtons.forEach((button) => {
      button.addEventListener('click', shareProduct);
    });

    syncVariant();
  };

  const initAll = () => {
    document.querySelectorAll('[data-fw-product]').forEach(initProduct);
  };

  initAll();

  if (!window.__fwProductJsBound) {
    window.__fwProductJsBound = true;
    document.addEventListener('shopify:section:load', (event) => {
      const sectionRoot = event.target?.querySelector?.('[data-fw-product]') || event.target;
      if (sectionRoot?.matches?.('[data-fw-product]')) {
        delete sectionRoot.dataset.fwProductReady;
        initProduct(sectionRoot);
        return;
      }
      sectionRoot?.querySelectorAll?.('[data-fw-product]').forEach((root) => {
        delete root.dataset.fwProductReady;
        initProduct(root);
      });
    });
  }
})();
